import {
  LayoutDashboard, BookOpen, Receipt, Users, Briefcase, FileText,
  Hammer, ClipboardList, Truck, Boxes,
  Network, MapPin, UserCheck, DollarSign, Wrench, ShieldCheck,
  Wifi, Smartphone,
  Printer, Cog, Factory, ShoppingBag, ShoppingCart, Handshake,
  Building2, Home, ScrollText,
  Earth, TrendingUp,
  Link2, Scale, CheckCircle2, Settings, History, Box, Inbox, ClipboardCheck, HardHat, Clock, Wallet, HandCoins,
  type LucideIcon,
  UserRound,
} from 'lucide-react';
import type { Metier, Role } from '@/types';

export interface NavItem {
  icon:    LucideIcon;
  label:   string;
  path:    string;
  /** Restriction par rôle si présent (sinon visible par tous les rôles authentifiés de la filiale) */
  roles?:  Role[];
  /** Entrée propre au béton prêt à l'emploi : masquée tant que BPE_EN_SERVICE vaut false (constants/activites.ts). */
  bpe?:    boolean;
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

/**
 * Sections communes à toutes les filiales.
 *
 * Elles étaient réunies en un seul bloc « Filiale » de onze entrées, dans
 * lequel un tableau de bord, une file de validation et un annuaire de contacts
 * se suivaient sans rapport. Trois groupes désormais, du plus urgent au plus
 * documentaire : ce qui attend une décision, les comptes, puis les référentiels.
 *
 * Source de vérité : brief CTO-METIER-FINAL-001.
 */
export const A_TRAITER_NAV: NavSection = {
  title: 'À traiter',
  items: [
    // Un seul écran de validation depuis le 21/09/2026 : proformas, bons de
    // commande et déclarations du terrain y sont réunis. « Validations DAF »,
    // qui vivait sous le Portail Terrain, portait presque le même nom pour le
    // même geste.
    { icon: CheckCircle2,    label: 'Validations',       path: '/validation',              roles: ['admin_holding', 'dg_filiale', 'daf'] },
    { icon: Inbox,           label: 'Demandes terrain',  path: '/achats/demandes',         roles: ['admin_holding', 'dg_filiale', 'daf', 'acheteur', 'manager'] },
    // 29/09/2026 — les journaliers déclarés par les chefs de chantier. C'est
    // une file d'argent à sortir : elle se range avec ce qu'on doit traiter,
    // pas dans un module métier qu'on ouvre quand on y pense.
    { icon: HardHat,         label: "Main-d'œuvre",      path: '/operations/main-oeuvre',  roles: ['admin_holding', 'dg_filiale', 'daf', 'responsable_production', 'manager', 'rh', 'auditeur'] },
  ],
};

export const FINANCE_NAV: NavSection = {
  title: 'Finance',
  items: [
    { icon: LayoutDashboard, label: 'Tableau de bord',   path: '/finance',                 roles: ['admin_holding', 'auditeur', 'dg_filiale', 'daf'] },
    { icon: BookOpen,        label: 'Journal comptable', path: '/finance/journal',         roles: ['admin_holding', 'auditeur', 'dg_filiale', 'daf'] },
    { icon: Receipt,         label: 'Factures fournisseurs', path: '/finance/fournisseurs', roles: ['admin_holding', 'auditeur', 'dg_filiale', 'daf', 'acheteur'] },
    { icon: Scale,           label: 'États comptables',  path: '/finance/etats',           roles: ['admin_holding', 'auditeur', 'dg_filiale', 'daf'] },
    { icon: Link2,           label: 'Lettrage',          path: '/finance/lettrage',        roles: ['admin_holding', 'auditeur', 'dg_filiale', 'daf'] },
    { icon: Receipt,         label: 'Factures',          path: '/finance/invoices',        roles: ['admin_holding', 'auditeur', 'dg_filiale', 'daf', 'acheteur', 'commercial'] },
    { icon: TrendingUp,      label: 'Balance âgée',      path: '/finance/balance-agee',    roles: ['admin_holding', 'auditeur', 'dg_filiale', 'daf', 'manager'] },
    { icon: FileText,        label: 'Tarifs contrats',   path: '/finance/tarifs-contrats', roles: ['admin_holding', 'dg_filiale', 'daf', 'commercial'] },
  ],
};

export const RESSOURCES_NAV: NavSection = {
  title: 'Ressources',
  items: [
    // Le chef d'équipe accède au patrimoine : c'est lui qui sait où est le
    // matériel et qui le déplace d'un chantier à l'autre.
    { icon: Box,             label: 'Patrimoine',        path: '/patrimoine',              roles: ['admin_holding', 'auditeur', 'dg_filiale', 'daf', 'manager', 'chef_equipe'] },
    { icon: Users,           label: 'Personnel RH',      path: '/hr',                      roles: ['admin_holding', 'auditeur', 'dg_filiale', 'daf', 'manager', 'rh'] },
    // Pointage des présences (16/10/2026) — P1 : horaires, jours fériés, numéros de pointage.
    { icon: Clock,           label: 'Pointage',          path: '/hr/pointage',             roles: ['admin_holding', 'auditeur', 'dg_filiale', 'daf', 'rh'] },
    // PAIE-2 (19/10/2026) : paramètres légaux et rubriques de paie (modification : DAF et PDG).
    { icon: Wallet,          label: 'Paie',              path: '/hr/paie',                 roles: ['admin_holding', 'auditeur', 'dg_filiale', 'daf', 'rh'] },
    // Arriérés de salaire (19/10/2026) : salaires dus et non payés, règlements validés par le DAF.
    { icon: HandCoins,       label: 'Arriérés de salaire', path: '/hr/arrieres',           roles: ['admin_holding', 'auditeur', 'dg_filiale', 'daf', 'rh'] },
    { icon: Briefcase,       label: 'CRM',               path: '/crm',                     roles: ['admin_holding', 'auditeur', 'dg_filiale', 'daf', 'manager', 'acheteur', 'commercial'] },
  ],
};

/**
 * Mon espace — 25/09/2026.
 *
 * L'écran « Mon compte » n'était joignable qu'en cliquant le bloc du bas de
 * la barre, dont l'apparence n'avait pas changé : rien n'indiquait qu'il
 * menait quelque part. Une fonction qu'on ne peut pas voir n'existe pas pour
 * celui qui l'utilise.
 *
 * Aucun `roles` : chacun a un compte, chacun doit pouvoir changer son mot de
 * passe.
 */
export const MON_ESPACE_NAV: NavSection = {
  title: 'Mon espace',
  items: [
    { icon: UserRound, label: 'Mon compte', path: '/mon-compte' },
  ],
};

/** Conservée pour les imports existants : les sections communes, dans l'ordre. */
export const COMMON_SECTIONS: NavSection[] = [A_TRAITER_NAV, FINANCE_NAV, RESSOURCES_NAV];

/**
 * SIMG-TERRAIN-2026-001 — Sidebar back-office Portail Terrain.
 * Visible pour les rôles habilités à gérer les intervenants terrain
 * (aujourd'hui uniquement LeadBeton CI utilise le portail — Sidebar
 * masque automatiquement les items dont le composant refuse le rôle).
 */
export const TERRAIN_ADMIN_NAV: NavSection = {
  title: 'Portail Terrain',
  items: [
    { icon: Link2,        label: 'Intervenants & accès', path: '/terrain/intervenants', roles: ['admin_holding', 'daf', 'dg_filiale', 'manager'] },
    // « Validations DAF » a rejoint l'écran unique « Validations » : deux
    // entrées au nom presque identique pour le même geste, c'était une de trop.
    // La page reste accessible par son adresse pour qui ne veut voir que les
    // chantiers.
    { icon: Settings,     label: 'Paramètres',            path: '/terrain/parametres',    roles: ['admin_holding', 'daf', 'dg_filiale', 'manager'] },
    { icon: History,      label: 'Journal d\'accès',      path: '/terrain/journal',       roles: ['admin_holding', 'daf', 'dg_filiale', 'manager'] },
  ],
};

/**
 * Sidebar Holding (admin_holding et auditeur uniquement).
 */
export const HOLDING_NAV: NavSection = {
  title: 'Holding',
  items: [
    { icon: Earth,     label: 'Vue consolidée', path: '/holding',          roles: ['admin_holding', 'auditeur'] },
    { icon: Building2, label: 'Fiches sociétés', path: '/holding/filiales', roles: ['admin_holding', 'auditeur'] },
    { icon: UserCheck, label: 'Utilisateurs',   path: '/holding/users',    roles: ['admin_holding'] },
  ],
};

/**
 * Sidebar Opérations — varie selon le métier de la filiale.
 * Chargée dynamiquement après /v1/filiales/me.
 */
export const METIER_NAV: Record<Metier, NavSection[]> = {
  // Béton — dix-huit écrans, regroupés par moment de la journée plutôt
  // qu'en une seule colonne où « Gâchées » suivait « Catalogue produits ».
  beton: [
    {
      title: 'Béton · Pilotage',
      items: [
        { icon: Hammer,        label: "Vue d'ensemble",       path: '/operations' },
        { icon: TrendingUp,    label: 'Tableau de bord DG',   path: '/operations/dashboard-dg', roles: ['admin_holding', 'dg_filiale', 'daf', 'auditeur'] },
      ],
    },
    {
      title: 'Béton · Ventes & chantiers',
      items: [
        { icon: ClipboardList, label: 'Commandes',            path: '/operations/commandes' },
        { icon: ClipboardList, label: 'Chantiers',            path: '/operations/chantiers' },
        { icon: Truck,         label: 'Expéditions / BL',     path: '/operations/expeditions' },
        { icon: ShieldCheck,   label: 'Réclamations',         path: '/operations/reclamations' },
      ],
    },
    {
      title: 'Béton · Production',
      items: [
        { icon: Cog,           label: 'Production (OF)',      path: '/operations/production' },
        { icon: Factory,       label: 'Production du jour',   path: '/operations/production-journaliere' },
        { icon: Cog,           label: 'Gâchées',              path: '/operations/gachees' },
        { icon: Hammer,        label: 'Contrôle qualité',     path: '/operations/qc' },
      ],
    },
    {
      title: 'Béton · Stocks & parc',
      items: [
        { icon: Boxes,         label: 'Parc',                 path: '/operations/parc' },
        { icon: Boxes,         label: 'Stocks (audit)',       path: '/operations/stocks' },
        // 29/09/2026 — un comptage physique qui ne se voit nulle part est perdu.
        { icon: ClipboardCheck, label: "Écarts d'inventaire",  path: '/operations/inventaire', roles: ['admin_holding', 'dg_filiale', 'daf', 'responsable_production', 'auditeur', 'manager'] },
        { icon: Boxes,         label: 'Matières premières',   path: '/operations/matieres' },
      ],
    },
    {
      title: 'Béton · Achats & matériel',
      items: [
        { icon: Building2,     label: 'Fournisseurs',         path: '/operations/fournisseurs' },
        { icon: Receipt,       label: 'Achats / BCA',         path: '/operations/achats' },
        { icon: Boxes,         label: 'Catalogue produits',   path: '/operations/catalogue' },
        { icon: Wrench,        label: 'Maintenance moules',   path: '/operations/maintenance-moules' },
        { icon: Truck,         label: 'Flotte toupies (BPE)', path: '/operations/flotte', bpe: true },
      ],
    },
  ],
  transport_minier: [{
    title: 'Opérations · Transport minier',
    items: [
      { icon: Network,     label: 'Vue d\'ensemble',    path: '/operations' },
      { icon: UserCheck,   label: 'Équipes',             path: '/operations/equipes' },
      { icon: Truck,       label: 'Flotte camions',      path: '/operations/flotte' },
      { icon: MapPin,      label: 'Rotations du jour',   path: '/operations/rotations' },
      { icon: ClipboardList,label: 'Rapport journalier', path: '/operations/rapport' },
      { icon: Wrench,      label: 'Maintenance',         path: '/operations/maintenance' },
      { icon: DollarSign,  label: 'Carburant',           path: '/operations/carburant' },
    ],
  }],
  telecom_wifi: [{
    title: 'Opérations · WeFly',
    items: [
      { icon: Wifi,        label: 'Réseau',              path: '/operations' },
      { icon: MapPin,      label: 'Carte hotspots',      path: '/operations/hotspots' },
      { icon: Smartphone,  label: 'Abonnés',             path: '/operations/abonnes' },
      { icon: DollarSign,  label: 'Revenus Mobile Money',path: '/operations/revenus' },
      // Interventions et Conformité ARTCI retirées : aucune route derrière.
    ],
  }],
  impression: [{
    title: 'Opérations · Imprimerie & Textile',
    items: [
      { icon: Printer,       label: 'Vue d\'ensemble',        path: '/operations' },
      // Sprint 2 — Devis (brief §C) + Sous-traitants (brief §H)
      { icon: FileText,      label: 'Devis',                  path: '/operations/devis-impression' },
      { icon: ClipboardList, label: 'Commandes',              path: '/operations/commandes-impression' },
      { icon: Cog,           label: 'Ordres de fabrication',  path: '/operations/of-impression' },
      { icon: Printer,       label: 'Parc machines',          path: '/operations/machines' },
      { icon: Handshake,     label: 'Sous-traitants',         path: '/operations/sous-traitants' },
      { icon: Boxes,         label: 'Stock consommables',     path: '/operations/consommables' },
      // Boutique e-shop — Sprint 1 (brief SIM-PLATFORM-BOUTIQUE-2026-001)
      { icon: ShoppingBag,   label: 'Catalogue Boutique',     path: '/operations/boutique/catalogue' },
      { icon: ShoppingCart,  label: 'Commandes Boutique',     path: '/operations/boutique/commandes' },
      { icon: Users,         label: 'Clients Boutique',       path: '/operations/boutique/clients' },
      { icon: Boxes,         label: 'Stocks Boutique',        path: '/operations/boutique/stocks' },
    ],
  }],
  immobilier: [{
    title: 'Opérations · Immobilier & Construction',
    items: [
      { icon: Building2,    label: 'Vue d\'ensemble',    path: '/operations' },
      { icon: Building2,    label: 'Programmes',         path: '/operations/programmes' },
      { icon: Home,         label: 'Lots',               path: '/operations/lots' },
      { icon: ScrollText,   label: 'Réservations VEFA',  path: '/operations/vefa' },
    ],
  }],
  generic: [{
    title: 'Opérations',
    items: [],
  }],
};
